"use client";

import { useMemo } from "react";
import enMessages from "@/messages/en.json";
import { useI18n } from "@/components/providers/i18n-provider";
import { getTranslations } from "@/lib/i18n/translations";

type SiteContent = typeof enMessages.site;

/**
 * The whole `site` namespace for the current locale, with English underneath it.
 *
 * The marketing pages are mostly lists: five ladder rungs, four safety pillars, nine
 * comparison rows. Reading those one key at a time ("safetyPage.pillars.0.title") means
 * the page and the copy can drift apart silently, and a translator who drops one entry
 * shortens the list rather than showing English in its place.
 *
 * This merges the locale over English per top level page instead, so a page that is only
 * half translated renders the translated half and English for the rest, and a list that is
 * missing entirely falls back to the complete English one. English is bundled statically
 * (see lib/i18n/translations.ts) so this is also what the server renders, which is what
 * search engines and answer engines read.
 */
export function useSiteContent(): SiteContent {
  const { locale, translationVersion } = useI18n();

  return useMemo(() => {
    const localised = getTranslations(locale, "site") as Partial<SiteContent>;
    const english = enMessages.site;

    // A shallow merge per page section is enough: every page owns one top level key, and
    // partial objects inside a page still fall back through getTranslation's own logic.
    const merged: Record<string, unknown> = { ...english };
    for (const [key, value] of Object.entries(localised ?? {})) {
      if (value && typeof value === "object" && !Array.isArray(value)) {
        merged[key] = { ...(english as Record<string, unknown>)[key] as object, ...value };
      } else if (value !== undefined && value !== null) {
        merged[key] = value;
      }
    }
    return merged as SiteContent;
    // getTranslations reads a module level cache that this component cannot see changing.
    // translationVersion is the signal that a locale file has finished loading, so it has
    // to stay in the dependency list even though the body never reads it: without it the
    // page would stay in English until something else happened to re-render it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale, translationVersion]);
}
